import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery, gql } from '@apollo/client';
import Heading from '../../atoms/Heading';
import TierSettingsPanel from '../../organisms/TierSettingsPanel/TierSettingsPanel';
import Button from '../../atoms/Button';
import LoadingSkeleton from '../../atoms/LoadingSkeleton';
import MessageHistoryLink from '../../molecules/MessageHistoryLink';
import './NotificationTierSection.scss';
import { useCan, Privilege } from '@feedflo/access-control';
import { NO_NOTIFICATIONS_TIER_NAME } from '../../utils/constants';
import { alertThresholdSecondsToHours } from '../../utils/tierNotificationSettings';

function isEditablePolicyTier(tier) {
  return tier?.editable !== false && tier?.name !== NO_NOTIFICATIONS_TIER_NAME;
}

export const GET_BARN_NOTIFICATION_SETTINGS_GQL = gql`
  query GetBarnNotificationSettings($barn_id: uuid!, $org_id: uuid!) {
    # Get organization-level settings
    org_settings: organization_fault_notification_tier_settings_view(
      where: { organization_id: { _eq: $org_id } }
      order_by: { tier: { sort_order: desc } }
    ) {
      tier {
        id
        sort_order
        name
        description: public_description
        editable
      }
      plan_id
      code
      enabled
      alert_threshold_type
      alert_threshold_seconds
      renotify_enabled
      renotify_threshold_seconds
      notify_on_resolved
    }
    # Get all barn overrides (will be filtered by plan_id in component)
    barn: farm_by_pk(id: $barn_id) {
      id
      fault_notification_settings: direct_fault_notification_tier_settings(order_by: { tier: { sort_order: desc } }) {
        tier {
          id
          sort_order
          name
          description: public_description
          notification_plan_id
          editable
        }
        code
        enabled
        alert_threshold_type
        alert_threshold_seconds
        renotify_enabled
        renotify_threshold_seconds
        notify_on_resolved
      }
    }
  }
`;

// Export mutations for use in parent component
export const UPSERT_BARN_FAULT_SETTINGS_GQL = gql`
  mutation UpsertBarnFaultNotificationTierSettings($objects: [fault_barn_notification_tier_settings_insert_input!]!) {
    insert_fault_barn_notification_tier_settings(
      objects: $objects
      on_conflict: {
        constraint: fault_barn_notification_tier_settings_unique
        update_columns: [
          enabled
          alert_threshold_seconds
          alert_threshold_type
          renotify_enabled
          renotify_threshold_seconds
          notify_on_resolved
        ]
      }
    ) {
      affected_rows
    }
  }
`;

export const DELETE_BARN_FAULT_SETTINGS_GQL = gql`
  mutation DeleteBarnFaultNotificationTierSettings($barn_id: uuid!) {
    delete_fault_barn_notification_tier_settings(where: { barn_id: { _eq: $barn_id } }) {
      affected_rows
    }
  }
`;

function NotificationTierSection({
  barnId,
  organizationId,
  formikValues,
  setFieldValue,
  canUpdateBarn: propCanUpdateBarn,
}) {
  // Check permissions
  const { can } = useCan();
  const canUpdateBarn = propCanUpdateBarn ?? can('barn', barnId, Privilege.BARN_UPDATE);

  // --- Apollo/GraphQL hooks ---
  const { loading, data, error } = useQuery(GET_BARN_NOTIFICATION_SETTINGS_GQL, {
    variables: { barn_id: barnId, org_id: organizationId },
    skip: !barnId || !organizationId,
    fetchPolicy: 'network-only',
    context: { headers: { 'x-hasura-role': 'user' } },
  });

  // --- Derived state ---
  // Extract plan_id from org_settings (all org_settings should have the same plan_id)
  const currentPlanId = data?.org_settings?.[0]?.plan_id;

  // Filter barn overrides to only show those for the current plan_id
  const barnOverridesForCurrentPlan = useMemo(() => {
    if (!data?.barn?.fault_notification_settings || !currentPlanId) {
      return [];
    }
    // Filter by plan_id - the query should already filter, but this is a safety check
    return data.barn.fault_notification_settings.filter(
      (setting) => setting.tier?.notification_plan_id === currentPlanId,
    );
  }, [data, currentPlanId]);

  // Determine if we're overriding based on form values or data
  const isOverriding = useMemo(() => {
    // Check if form has override settings
    if (formikValues?.notificationTierSettings && Object.keys(formikValues.notificationTierSettings).length > 0) {
      return true;
    }
    // Fall back to checking data
    return barnOverridesForCurrentPlan.length > 0;
  }, [formikValues, barnOverridesForCurrentPlan]);

  // Transform the tier data into the format expected by TierSettingsPanel
  // Use formik values if available, otherwise use data
  const transformedSettings = useMemo(() => {
    // If we have formik values, use those (they represent the current state including unsaved changes)
    if (formikValues?.notificationTierSettings && Object.keys(formikValues.notificationTierSettings).length > 0) {
      const settings = {};
      const tierOrder = [];

      // Get tier metadata from org_settings, filtering out non-editable tiers
      const tierMetadata = {};
      data?.org_settings
        ?.filter((setting) => isEditablePolicyTier(setting.tier))
        .forEach((setting) => {
          const tierId = setting.tier.id;
          if (!tierMetadata[tierId]) {
            tierMetadata[tierId] = {
              name: setting.tier.name,
              description: setting.tier.description,
            };
          }
        });

      // Build settings from formik values, only for editable tiers
      Object.entries(formikValues.notificationTierSettings).forEach(([tierId, faultSettings]) => {
        // Only include tiers that are editable
        if (tierMetadata[tierId]) {
          if (!settings[tierId]) {
            settings[tierId] = {
              name: tierMetadata[tierId]?.name || 'Unknown Tier',
              description: tierMetadata[tierId]?.description || '',
              tierId: tierId,
              faultSettings: {},
            };
            tierOrder.push(tierId);
          }
          settings[tierId].faultSettings = faultSettings;
        }
      });

      return tierOrder.map((tierId) => settings[tierId]);
    }

    // Otherwise, use data from query
    const settingsSource = isOverriding ? barnOverridesForCurrentPlan : data?.org_settings;

    if (!settingsSource || settingsSource.length === 0) {
      return [];
    }

    const settings = {};
    const tierOrder = [];

    settingsSource
      .filter((faultSetting) => isEditablePolicyTier(faultSetting.tier))
      .forEach((faultSetting) => {
        const tierId = faultSetting.tier.id;

        if (!settings[tierId]) {
          settings[tierId] = {
            name: faultSetting.tier.name,
            description: faultSetting.tier.description,
            tierId: tierId,
            faultSettings: {},
          };
          // Track the order tiers appear in the source data
          tierOrder.push(tierId);
        }

        // Add fault setting to the dictionary with fault code as key
        settings[tierId].faultSettings[faultSetting.code.toString()] = {
          code: faultSetting.code,
          alert: {
            enabled: faultSetting.enabled,
            hours: alertThresholdSecondsToHours(faultSetting.alert_threshold_seconds, faultSetting.code),
            type: faultSetting.alert_threshold_type,
            option:
              faultSetting.alert_threshold_type === 'nofeed_time' && faultSetting.alert_threshold_seconds < 0
                ? 'before_empty'
                : faultSetting.alert_threshold_type === 'nofeed_time'
                  ? 'after_empty'
                  : 'after_duration',
          },
          reminders: {
            enabled: faultSetting.renotify_enabled,
            hours: faultSetting.renotify_threshold_seconds / 3600,
          },
          resolved: {
            enabled: faultSetting.notify_on_resolved,
          },
        };
      });

    // Preserve the original order from the database
    return tierOrder.map((tierId) => settings[tierId]);
  }, [data, isOverriding, barnOverridesForCurrentPlan, formikValues]);

  // --- Function definitions ---
  const handleTierSettingChange = (newFaultSettings, tierId) => {
    // Update Formik form value to mark form as dirty
    const currentSettings = formikValues?.notificationTierSettings || {};
    setFieldValue('notificationTierSettings', {
      ...currentSettings,
      [tierId]: newFaultSettings,
    });
  };

  const handleToggleOverride = () => {
    if (isOverriding) {
      // Remove barn overrides - revert to org defaults
      setFieldValue('notificationTierSettings', {});
      setFieldValue('notificationTierSettingsDelete', true);
    } else {
      // Create barn overrides by copying current org settings
      if (data?.org_settings && data.org_settings.length > 0) {
        const newSettings = {};
        data.org_settings.filter((setting) => isEditablePolicyTier(setting.tier)).forEach((setting) => {
          const tierId = setting.tier.id;
          if (!newSettings[tierId]) {
            newSettings[tierId] = {};
          }
          newSettings[tierId][setting.code.toString()] = {
            code: setting.code,
            alert: {
              enabled: setting.enabled,
              hours: alertThresholdSecondsToHours(setting.alert_threshold_seconds, setting.code),
              type: setting.alert_threshold_type,
              option:
                setting.alert_threshold_type === 'nofeed_time' && setting.alert_threshold_seconds < 0
                  ? 'before_empty'
                  : setting.alert_threshold_type === 'nofeed_time'
                    ? 'after_empty'
                    : 'after_duration',
            },
            reminders: {
              enabled: setting.renotify_enabled,
              hours: setting.renotify_threshold_seconds / 3600,
            },
            resolved: {
              enabled: setting.notify_on_resolved,
            },
          };
        });
        setFieldValue('notificationTierSettings', newSettings);
        setFieldValue('notificationTierSettingsDelete', false);
      }
    }
  };

  const currentSettings = transformedSettings;

  // --- Render ---
  if (loading) {
    return (
      <div className="NotificationTierSection">
        <div className="NotificationTierSection-content">
          <LoadingSkeleton style={{ width: '100%', height: '300px' }} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="NotificationTierSection">
        <div className="NotificationTierSection-content">
          <div style={{ color: 'red' }}>Error loading notification settings: {error.message}</div>
        </div>
      </div>
    );
  }

  if (!data?.org_settings || data.org_settings.length === 0) {
    return (
      <div className="NotificationTierSection">
        <div className="NotificationTierSection-content">
          <div className="NotificationTierSection-headerDescription">
            No organization notification plan has been configured. Please configure a plan at the organization level
            first.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="NotificationTierSection">
      <div className="NotificationTierSection-content">
        <div className="">
          <div className="NotificationTierSection-cardHeader">
            <div className="NotificationTierSection-headerInfo">
              <Heading className="TabSection-heading">
                {isOverriding ? 'Barn-Specific Overrides' : 'Using Organization Defaults'}
              </Heading>
              <div className="NotificationTierSection-headerDescription">
                {isOverriding
                  ? 'This barn has custom notification settings that override the organization policy.'
                  : 'This barn uses the organization-wide notification policy. Settings below are read-only until you create an override.'}
              </div>
              <MessageHistoryLink filters={{ orgId: organizationId, barnId }} />
            </div>
            {canUpdateBarn && (
              <div className="NotificationTierSection-headerActions">
                <Button
                  variant="vivid"
                  color={isOverriding ? 'neutral' : 'success'}
                  content={isOverriding ? 'Use Organization Defaults' : 'Override Organization Policy'}
                  onClick={handleToggleOverride}
                />
              </div>
            )}
          </div>

          <div className={`NotificationTierSection-cardContent`}>
            <div className="NotificationTierSection-tiersList">
              {currentSettings.length > 0 ? (
                currentSettings.map((settings) => (
                  <div key={settings.tierId} className="NotificationTierSection-tierItem">
                    <TierSettingsPanel
                      tierName={settings.name}
                      tierDescription={settings.description}
                      settings={settings.faultSettings}
                      onSettingChange={(newFaultSettings) => {
                        handleTierSettingChange(newFaultSettings, settings.tierId);
                      }}
                      disabled={!isOverriding || !canUpdateBarn}
                      showTitle={true}
                    />
                  </div>
                ))
              ) : (
                <div>No notification policy settings available</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

NotificationTierSection.propTypes = {
  barnId: PropTypes.string.isRequired,
  organizationId: PropTypes.string.isRequired,
  canUpdateBarn: PropTypes.bool,
  formikValues: PropTypes.object,
  setFieldValue: PropTypes.func,
};

export default NotificationTierSection;
